import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { assert } from '~/util';
import type { MeasuredComponent, SimulatedComponent } from './simulation';

export type ComplexRepr = 'cartesian' | 'polar' | 'polartau';
export type BasisRepr = 'computational' | 'physical';

/** state to index mapping: [ground, hyperfine, rydberg]*/
export function pickStateNames(basis: BasisRepr): [string, string, string] {
  switch (basis) {
    case 'computational':
      return ['0', '1', 'r'];
    case 'physical':
      return ['g', 'h', 'r'];
    default: {
      const n: never = basis;
      throw new Error(`Unexpected basis: ${n}`);
    }
  }
}

/**
 * Transform qutrit index from simulation data
 * straight into string representation.
 */
export function printQutritIndices(
  index: bigint,
  numAtoms: number,
  names: [string, string, string]
): string {
  let printed = '';
  for (let i = 0; i < numAtoms; i++) {
    const q = Number(index & 3n);
    assert(q < 3);
    printed = names[q] + printed;
    index >>= 2n;
  }
  return printed;
}

/**
 * Transform qubit index from measurement data
 * straight into string representation.
 */
export function printMeasureIndices(index: number, numAtoms: number): string {
  let printed = '';
  for (let i = 0; i < numAtoms; i++) {
    printed = ((index & 1) === 0 ? '0' : '1') + printed;
    index >>= 1;
  }
  return printed;
}

type UIStateContextProps = {
  pulseSelection: Set<string>;
  atomSelection: Set<string>;
  hoveredAtom: number | null;
  setHoveredAtom: Dispatch<SetStateAction<number | null>>;
  focusedPanel: string | null;
  hoveredState: SimulatedComponent | MeasuredComponent | null;
  setHoveredState: Dispatch<
    SetStateAction<SimulatedComponent | MeasuredComponent | null>
  >;
  basisRepr: BasisRepr;
  setBasisRepr: Dispatch<SetStateAction<BasisRepr>>;
  previewAmplitude: number;
  setPreviewAmplitude: Dispatch<SetStateAction<number>>;
  pulseDragsActive: number;
  setPulseDragsActive: Dispatch<SetStateAction<number>>;
  showAtomsInteractions: boolean;
  setShowAtomsInteractions: Dispatch<SetStateAction<boolean>>;
  selectedChannel: string | null;
  focusPanel: (panel: string) => void;
  replaceAtomSelection: (newSelection: Set<string>) => void;
  replacePulseSelection: (newSelection: Set<string>) => void;
  selectAtom: (uuid: string, additive?: boolean) => void;
  removeAtomSelection: (uuid: string) => void;
  selectPulse: (uuid: string, additive?: boolean) => void;
  removePulseSelection: (uuid: string) => void;
  selectChannel: (uuid: string | null) => void;
};

interface UiStateProviderProps {
  children: ReactNode;
}

const UiStateContext = createContext<UIStateContextProps | null>(null);
UiStateContext.displayName = 'UiState';

export const UiStateProvider = ({ children }: UiStateProviderProps) => {
  const [focusedPanel, setFocusedPanel] = useState<string | null>(null);
  const [pulseSelection, setPulseSelection] = useState(new Set<string>());
  const [atomSelection, setAtomSelection] = useState(new Set<string>());
  const [hoveredAtom, setHoveredAtom] = useState<number | null>(null);
  const [hoveredState, setHoveredState] = useState<
    SimulatedComponent | MeasuredComponent | null
  >(null);
  const [previewAmplitude, setPreviewAmplitude] = useState(1.5 * Math.PI);
  const [basisRepr, setBasisRepr] = useState<BasisRepr>('computational');
  const [pulseDragsActive, setPulseDragsActive] = useState(0);
  const [showAtomsInteractions, setShowAtomsInteractions] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);

  const focusPanel = useCallback((panel: string) => setFocusedPanel(panel), []);

  const replaceAtomSelection = useCallback((newSelection: Set<string>) => {
    setPulseSelection(new Set());
    setAtomSelection(newSelection);
  }, []);

  const replacePulseSelection = useCallback((newSelection: Set<string>) => {
    setAtomSelection(new Set());
    setPulseSelection(newSelection);
  }, []);

  const selectAtom = useCallback((uuid: string, additive = false) => {
    setPulseSelection(new Set());
    setSelectedChannel(null);
    setAtomSelection((prev) => {
      const next = additive ? new Set(prev) : new Set<string>();
      next.add(uuid);
      return next;
    });
  }, []);

  const removeAtomSelection = useCallback((uuid: string) => {
    setAtomSelection((prev) => {
      const next = new Set(prev);
      next.delete(uuid);
      return next;
    });
  }, []);

  const selectPulse = useCallback((uuid: string, additive = false) => {
    setAtomSelection(new Set());
    setSelectedChannel(null);
    setPulseSelection((prev) => {
      const next = additive ? new Set(prev) : new Set<string>();
      next.add(uuid);
      return next;
    });
  }, []);

  const removePulseSelection = useCallback((uuid: string) => {
    setPulseSelection((prev) => {
      const next = new Set(prev);
      next.delete(uuid);
      return next;
    });
  }, []);

  const selectChannel = useCallback((uuid: string | null) => {
    setAtomSelection(new Set());
    setPulseSelection(new Set());
    setSelectedChannel(uuid);
  }, []);

  const value: UIStateContextProps = useMemo(
    () => ({
      pulseSelection,
      atomSelection,
      hoveredAtom,
      setHoveredAtom,
      focusedPanel,
      hoveredState,
      setHoveredState,
      basisRepr,
      setBasisRepr,
      previewAmplitude,
      setPreviewAmplitude,
      pulseDragsActive,
      setPulseDragsActive,
      showAtomsInteractions,
      setShowAtomsInteractions,
      selectedChannel,
      focusPanel,
      replaceAtomSelection,
      replacePulseSelection,
      selectAtom,
      removeAtomSelection,
      selectPulse,
      removePulseSelection,
      selectChannel
    }),
    [
      pulseSelection,
      atomSelection,
      hoveredAtom,
      focusedPanel,
      hoveredState,
      basisRepr,
      previewAmplitude,
      pulseDragsActive,
      showAtomsInteractions,
      selectedChannel,
      focusPanel,
      replaceAtomSelection,
      replacePulseSelection,
      selectAtom,
      removeAtomSelection,
      selectPulse,
      removePulseSelection,
      selectChannel
    ]
  );

  return (
    <UiStateContext.Provider value={value}>{children}</UiStateContext.Provider>
  );
};

export const useUiState = () => {
  const ctx = useContext(UiStateContext);
  if (!ctx) {
    throw new Error('UiState not provided');
  }
  return ctx;
};
